import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
} from '@angular/core';

import { Question } from '@app/forms/subscription/definition/interface';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as _ from 'lodash';
import { mergeMap, tap } from 'rxjs';
import { PledgeRead } from '../../definition/interface/Pledge';
import { SubscriptionFacadeService } from '../../services/subscription-facade.service';

@UntilDestroy()
@Component({
  selector: 'menu-list',
  templateUrl: './menu-list.component.html',
  styleUrls: ['./menu-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class MenuListComponent implements OnInit {
  @Input() step: string;
  @Input() disabled: boolean;

  questions: Question[] = [];
  groupedData;
  _ = _;

  constructor(public subscriptionFacade: SubscriptionFacadeService) {}

  ngOnInit(): void {
    this.subscriptionFacade.stepChanged$
      .pipe(
        untilDestroyed(this),
        mergeMap(step => {
          return this.subscriptionFacade.questionsChanged$;
        }),
        tap(questions => {
          if (questions && this.step) {
            this.questions = this.subscriptionFacade.getQuestions();
            this.groupDataByCategory();
          }
        }),
      )
      .subscribe();
  }
  private getQuestionTitle(question: Question): string {
    return !_.isNil(question.getTitle)
      ? question.getTitle(
          this.subscriptionFacade.getPledge(),
          this.subscriptionFacade.getUser(),
        )
      : question.title;
  }
  private groupDataByCategory(): void {
    this.groupedData = _.groupBy(this.questions, 'category');
    _.forEach(this.groupedData, questions =>
      _.remove(
        questions,
        (question: Question) => !this.getQuestionTitle(question),
      ),
    );
  }

  setQuestionStep(question) {
    const q = this.questions?.findIndex(
      q => this.getQuestionTitle(q) === this.getQuestionTitle(question),
    );
    if ((q || q == 0) && q !== -1) {
      this.subscriptionFacade.setStep(Number(q) + 1);
    }
  }
  isQuestionValid(q, pledge) {
    return q?.callbackValidation?.(pledge, this.subscriptionFacade.getUser());
  }
  isQuestionNotValid(q, pledge) {
    return q?.callbackValidation && !this.isQuestionValid(q, pledge);
  }
  questionNotValid(q: Question) {
    const pledge = this.subscriptionFacade.getPledge();
    return this.isQuestionNotValid(q, pledge);
  }
  isCallbackValidationDisabled(q: Question): boolean {
    return !q.callbackValidation;
  }
  isFirstQuestionNotValid(q: Question, step: string, index: number) {
    const pledge = this.subscriptionFacade.getPledge();
    const { firstInvalidQuestion, firstInvalidQuestionIndex } =
      this.getFirstInvalidQuestion(step, pledge);
    if (firstInvalidQuestionIndex !== -1 && index > firstInvalidQuestionIndex)
      return true;
    if (this.isCallbackValidationDisabled(q)) {
      return false;
    }
    if (this.isQuestionValid(q, pledge)) {
      return false;
    }
    return firstInvalidQuestion?.name !== q?.name;
  }

  getFirstInvalidQuestion(step: string, pledge: PledgeRead) {
    const questions = this.groupedData?.[step];
    const firstInvalidQuestion = questions?.find(q =>
      this.isQuestionNotValid(q, pledge),
    );
    const firstInvalidQuestionIndex = questions?.findIndex(
      q => q?.name === firstInvalidQuestion?.name,
    );
    return {
      firstInvalidQuestion,
      firstInvalidQuestionIndex,
    };
  }
}
